"use client";

import React, { useRef } from "react";
import { Check, ArrowRight } from "lucide-react";
import { usePathname } from "next/navigation";

const betaFeatures = [
  "AI-powered lead discovery",
  "LinkedIn + email outreach",
  "Smart rate limiting",
  "Priority support",
];

const Pricing = () => {
  const pathname = usePathname();
  const requestAccessHref = pathname === "/" ? "#request-access" : "/#request-access";
  const cardRef = useRef<HTMLElement | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!cardRef.current) return;

    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 18;

    card.style.setProperty("--x", `${x}px`);
    card.style.setProperty("--y", `${y}px`);
    card.style.setProperty("--bg-x", `${(x / rect.width) * 100}%`);
    card.style.setProperty("--bg-y", `${(y / rect.height) * 100}%`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;

    const card = cardRef.current;
    card.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg)";
    card.style.setProperty("--x", "50%");
    card.style.setProperty("--y", "50%");
    card.style.setProperty("--bg-x", "50%");
    card.style.setProperty("--bg-y", "50%");
  };

  return (
    <section id="pricing" className="section-light relative overflow-hidden py-16 sm:py-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_at_top,rgba(14,165,233,0.1),transparent_62%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 sm:mb-10 text-center">
          <h2 className="section-title text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3 tracking-[-0.02em]">
            Invite-only beta, free
          </h2>
          <p className="section-subtitle text-base sm:text-lg max-w-2xl mx-auto">
            Full access while we refine company discovery, scoring, and outreach with early users.
          </p>
        </div>

        <div className="mx-auto max-w-[24rem]">
          <article
            ref={cardRef}
            className="pricing-card pricing-card-featured holographic-card flex flex-col rounded-[2rem] border px-5 py-6 sm:px-6 sm:py-7"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div className="holo-content">
              <div className="mb-3 flex justify-center">
                <span className="pricing-pill whitespace-nowrap">
                  Early Access
                </span>
              </div>
              <div className="mb-4 text-center">
                <h3 className="mb-1 text-[0.95rem] font-bold tracking-[0.02em] uppercase text-text-light-primary/80">
                  Invite-only beta
                </h3>
                <div className="text-[1.3rem] leading-[1.1] font-bold tracking-[-0.03em] text-text-light-primary sm:text-[1.5rem]">
                  Free
                </div>
              </div>

              <div className="mx-auto w-full max-w-[16rem]">
                <p className="mb-5 text-center text-sm leading-relaxed text-text-light-secondary">
                  Use the full product while we refine discovery, scoring, and outreach with you.
                </p>

                <div className="mb-6 flex justify-center">
                  <ul className="space-y-2.5">
                    {betaFeatures.map((feature) => (
                      <li key={feature} className="flex items-center text-[13px] text-text-light-secondary">
                        <span className="pricing-feature-check mr-2.5 shrink-0">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={requestAccessHref}
                  className="w-full rounded-[20px] border border-accent-blue/35 btn-primary flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all duration-140"
                >
                  Request Access
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="holo-glow" aria-hidden="true"></div>
          </article>
        </div>

        <p className="text-center text-sm text-text-light-muted mt-6">
          No credit card required. Pricing details will be shared before launch.
        </p>
      </div>
    </section>
  );
};

export default Pricing;
